// routePreloader.ts - Intelligent route preloading for better performance
import { matchPath } from 'react-router-dom';

// Map of routes to their component chunks
const routeChunks: Record<string, () => Promise<any>> = {
  '/admin': () => import('../AdminPage'),
  '/study': () => import('../StudyModePageV2'),
  '/analytics': () => import('../AnalyticsDashboardV2'),
  '/quiz/:standard': () => import('../QuizPageEnhancedV2'),
  '/quiz-setup/:standard': () => import('../QuizSetupV2'),
  '/achievements': () => import('../AchievementsPageV2'),
  '/review': () => import('../ReviewModePageV2'),
  '/bookmarks': () => import('../BookmarksPageV2'),
  '/profile': () => import('../ProfilePageV2'),
  '/email-preferences': () => import('../EmailPreferencesV2'),
};

// Preloaded routes cache
const preloadedRoutes = new Set<string>();

/**
 * Preload a route component
 */
export const preloadRoute = (path: string) => {
  // Find matching route pattern
  const routePattern = Object.keys(routeChunks).find(pattern => 
    matchPath({ path: pattern }, path)
  );
  
  if (routePattern && !preloadedRoutes.has(routePattern)) {
    preloadedRoutes.add(routePattern);
    routeChunks[routePattern]();
  }
};

/**
 * Preload critical routes on app startup
 */
export const preloadCriticalRoutes = () => {
  // These are the most commonly accessed routes
  const criticalRoutes = [
    '/quiz/:standard',
    '/study',
    '/analytics'
  ];
  
  criticalRoutes.forEach(route => {
    if (routeChunks[route]) {
      preloadedRoutes.add(route);
      routeChunks[route]();
    }
  });
};

/**
 * Preload routes based on user behavior patterns
 */
export const preloadBasedOnBehavior = (currentPath: string) => {
  // Predictive preloading based on common navigation patterns
  const predictions: Record<string, string[]> = {
    '/home': ['/quiz-setup/:standard', '/study', '/analytics'],
    '/quiz-setup/:standard': ['/quiz/:standard'],
    '/quiz/:standard': ['/analytics', '/review'],
    '/study': ['/bookmarks', '/quiz-setup/:standard'],
    '/analytics': ['/achievements'],
    '/profile': ['/email-preferences'],
  };
  
  const routePattern = Object.keys(routeChunks).find(pattern => 
    matchPath({ path: pattern }, currentPath)
  );
  
  if (routePattern && predictions[routePattern]) {
    predictions[routePattern].forEach(nextRoute => {
      if (routeChunks[nextRoute] && !preloadedRoutes.has(nextRoute)) {
        // Delay preloading to avoid blocking current route
        setTimeout(() => {
          preloadedRoutes.add(nextRoute);
          routeChunks[nextRoute]();
        }, 1000);
      }
    });
  }
};

/**
 * Preload on link hover
 */
export const preloadOnHover = (href: string) => {
  // Extract path from href
  const path = href.replace(window.location.origin, '');
  preloadRoute(path);
};

/**
 * Hook for route preloading
 */
export const useRoutePreloader = () => {
  return {
    preloadRoute,
    preloadOnHover,
    preloadBasedOnBehavior
  };
};